import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo, useState } from "react";
import { Box, Typography, InputBase, Button } from "@mui/material";
import { Info } from "lucide-react";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
import CommonTable from "../../../common_components/CommonTable";
import Pagination from "../../../common_components/Pagination";
import TableToolbar from "../../../common_components/TableToolbar";
import { SUMMARY_ROW_POSITIONS, TABLE_TYPES, EXPAND_TRIGGERS } from "../../../common_components/tableConstants";
import DiffChip from "../../CommissionTracker/components/DiffChip";
import { commissionTableContainerSx, CurrencyText, SubCurrencyText } from "../../CommissionTracker/components/commissionTableShared";
const ITEMS_PER_PAGE = 50;
// Order-count counterparts to the shared CurrencyText / SubCurrencyText, used for
// order-based clients where nothing on the page is a dollar figure. Same type scale
// and colours — only the "$" symbol is dropped.
const CountText = React.memo(({ value, bold }) => {
    const isNeg = value < 0;
    return (_jsx(Box, { sx: { px: 1, textAlign: "left" }, children: _jsxs(Typography, { sx: {
                fontFamily: "Inter",
                fontWeight: bold ? 600 : 400,
                color: bold ? "#181d27" : "#27272a",
                whiteSpace: "nowrap",
                fontSize: "14px !important",
            }, children: [isNeg ? "-" : "", Math.abs(Math.round(value)).toLocaleString()] }) }));
});
const SubCountText = React.memo(({ value }) => {
    const isNeg = value < 0;
    return (_jsxs(Typography, { sx: { px: 1, fontFamily: "Inter", fontWeight: 400, color: "#98A2B3", whiteSpace: "nowrap", fontSize: "13px !important" }, children: [isNeg ? "-" : "", Math.abs(Math.round(value)).toLocaleString()] }));
});
// ── Grouped columns (mirrors the Figma report layout). The Daily/MTD columns
// are labelled by commission type ("Sales" / "Orders" / "Spend"). ──
const buildColumns = (metricWord) => [
    { id: "date", label: "Date", width: "100px", align: "left", sortable: true },
    {
        id: "actualGroup",
        label: "Actual/Projected",
        align: "center",
        children: [
            { id: "actual_daily", label: `Daily ${metricWord}`, width: "130px", align: "left", sortable: true },
            { id: "actual_mtd", label: `MTD ${metricWord}`, width: "120px", align: "left", sortable: true },
            { id: "diff", label: "Diff", width: "110px", align: "left", sortable: true },
        ],
    },
    {
        id: "originalGroup",
        label: "Original",
        align: "center",
        children: [
            { id: "original_daily", label: `Daily ${metricWord}`, width: "130px", align: "left", sortable: true },
            { id: "modifier", label: "Modifier", width: "220px", align: "left", sortable: false },
            { id: "original_mtd", label: `MTD ${metricWord}`, width: "120px", align: "left", sortable: true },
        ],
    },
    {
        id: "mtdCommissionGroup",
        label: "MTD Commission",
        align: "center",
        children: [
            { id: "mtd_original", label: "Original", width: "120px", align: "left", sortable: true },
            { id: "mtd_actual", label: "Act./Proj.", width: "120px", align: "left", sortable: true },
        ],
    },
    { id: "notes", label: "Notes", width: "700px", align: "left", sortable: false },
];
// Leaf columns the user is allowed to hide (Date stays pinned/visible).
const HIDEABLE_COLUMN_IDS = [
    "actual_daily", "actual_mtd", "diff",
    "original_daily", "modifier", "original_mtd",
    "mtd_original", "mtd_actual",
    "notes",
];
const SORT_ACCESSORS = {
    date: (r) => r.date,
    actual_daily: (r) => r.actualDailySales,
    actual_mtd: (r) => r.actualMtdSales,
    diff: (r) => r.diff.percentage,
    original_daily: (r) => r.originalDailySales,
    original_mtd: (r) => r.originalMtdSales,
    mtd_original: (r) => r.mtdCommissionOriginal ?? -1,
    mtd_actual: (r) => r.mtdCommissionActualProjected ?? -1,
};
// Muted "-" shown when an MTD commission value is missing.
const Dash = () => _jsx(Box, { sx: { px: 1 }, children: _jsx(Typography, { sx: { color: "#a4a7ae", fontWeight: 600, fontSize: "14px !important" }, children: "-" }) });
// Inline editable input (Modifier / Notes / Daily). Defined at module scope with a
// stable identity so React doesn't remount it on every keystroke. It owns its own
// draft state and only pushes to the parent on blur (via onCommit) — so typing never
// re-renders the parent table, which was making each keystroke laggy.
const EditableInput = React.memo(function EditableInput({ value, placeholder, onCommit, multiline, prefix, suffix, autoFocus, }) {
    const [draft, setDraft] = React.useState(value);
    const focusedRef = React.useRef(false);
    // Re-sync the draft when the external value changes (e.g. after a report
    // refetch), but never while the user is actively editing this field.
    React.useEffect(() => {
        if (!focusedRef.current)
            setDraft(value);
    }, [value]);
    return (_jsx(InputBase, { autoFocus: autoFocus, value: draft, placeholder: placeholder, onFocus: () => { focusedRef.current = true; }, onChange: (e) => setDraft(e.target.value), onBlur: (e) => { focusedRef.current = false; onCommit?.(e.target.value); }, multiline: multiline, minRows: multiline ? 2 : undefined, startAdornment: prefix ? _jsx(Box, { component: "span", sx: { color: "#98A2B3", mr: 0.5, fontSize: "14px" }, children: prefix }) : undefined, endAdornment: suffix ? _jsx(Box, { component: "span", sx: { color: "#98A2B3", ml: 0.5, fontSize: "14px" }, children: suffix }) : undefined, sx: {
            fontSize: "14px",
            width: "100%",
            border: "1px solid #D0D5DD",
            borderRadius: "6px",
            bgcolor: "#fff",
            px: 1.25,
            py: 0.75,
            ...(multiline ? { whiteSpace: "pre-wrap", wordBreak: "break-word", minHeight: "52px", resize: "vertical" } : {}),
            "& textarea, & input": { p: 0, color: "#27272a" },
            "& textarea::placeholder, & input::placeholder": { color: "#98A2B3", opacity: 1 },
        } }));
});
// Original (pre-edit) value formatted for the tooltip (keeps cents, e.g. "$ 32,672.82").
const fmtApiMoney = (v) => `$ ${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Same tooltip for order-based clients — a count, so no "$" and no cents.
const fmtApiCount = (v) => Math.round(v).toLocaleString();
// Actual/Projected → Daily cell for editable (past) days. The cell always shows the
// effective value (the manual entry when there is one) and clicking it reveals an
// editable text box that commits on blur.
//
// The ⓘ is only shown for days that were MANUALLY updated (the day carries a
// `change_value` → `manual_updated`); its tooltip is the original synced value that
// the manual entry replaced. Untouched days have nothing to compare against — their
// original IS the displayed figure — so they get no icon.
const DailyActualCell = React.memo(function DailyActualCell({ value, apiValue, placeholder, isOrders, isManual, onCommit, }) {
    const [editing, setEditing] = React.useState(false);
    if (editing) {
        return (_jsxs(Box, { sx: { px: 1, py: 0.5, display: "flex", flexDirection: "column", gap: 0.5 }, children: [_jsx(EditableInput, { autoFocus: true, value: String(Math.round(value)), placeholder: placeholder, 
                    // No "$" adornment for order-based clients — the field is a count.
                    prefix: isOrders ? undefined : "$", onCommit: (v) => { setEditing(false); onCommit(v); } }), isManual && (isOrders ? _jsx(SubCountText, { value: apiValue }) : _jsx(SubCurrencyText, { value: apiValue }))] }));
    }
    return (_jsxs(Box, { onClick: () => setEditing(true), sx: { display: "flex", alignItems: "center", cursor: "pointer" }, children: [isOrders ? _jsx(CountText, { value: value }) : _jsx(CurrencyText, { value: value }), isManual && (_jsx(HoverTooltip, { title: isOrders ? fmtApiCount(apiValue) : fmtApiMoney(apiValue), arrow: true, placement: "top", children: _jsx(Box, { sx: { display: "flex", alignItems: "center", color: "#98A2B3", cursor: "help", mr: 1 }, children: _jsx(Info, { size: 15 }) }) }))] }));
});
// Modifier cell — collapsed it shows "12.05 %" (or the muted "Modifier"
// placeholder); clicking reveals an editable text box (with a "%" suffix) that
// updates the draft on blur. Actual persistence still happens via "Save Modifier".
const ModifierCell = React.memo(function ModifierCell({ value, onCommit, }) {
    const [editing, setEditing] = React.useState(false);
    if (editing) {
        return (_jsx(Box, { sx: { px: 1, py: 0.5 }, children: _jsx(EditableInput, { autoFocus: true, value: value, placeholder: "Modifier", suffix: "%", onCommit: (v) => { setEditing(false); onCommit(v); } }) }));
    }
    const hasValue = value.trim() !== "";
    return (_jsx(Box, { onClick: () => setEditing(true), sx: { px: 1, minHeight: 24, display: "flex", alignItems: "center", cursor: "pointer" }, children: hasValue ? (_jsxs(Typography, { sx: { fontSize: "14px", color: "#27272a", whiteSpace: "nowrap" }, children: [value, _jsx(Box, { component: "span", sx: { color: "#98A2B3", ml: 0.5 }, children: "%" })] })) : (_jsx(Typography, { sx: { fontSize: "14px", color: "#98A2B3", whiteSpace: "nowrap" }, children: "Modifier" })) }));
});
// Notes cell — collapsed it shows the note (truncated) or a muted "Note"
// placeholder; clicking reveals a real resizable <textarea> (drag the bottom-right
// handle to grow/shrink) that commits on blur.
const NotesCell = React.memo(function NotesCell({ value, onCommit, }) {
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState(value);
    // Keep the draft in sync with external updates while not actively editing.
    React.useEffect(() => { if (!editing)
        setDraft(value); }, [value, editing]);
    if (editing) {
        return (_jsx(Box, { sx: { px: 1, py: 0.5 }, children: _jsx(Box, { component: "textarea", autoFocus: true, value: draft, placeholder: "Note", onChange: (e) => setDraft(e.target.value), onBlur: (e) => { setEditing(false); onCommit(e.target.value); }, sx: {
                    width: "100%",
                    minHeight: "52px",
                    resize: "vertical",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    lineHeight: 1.5,
                    color: "#27272a",
                    border: "1px solid #D0D5DD",
                    borderRadius: "6px",
                    bgcolor: "#fff",
                    px: 1.25,
                    py: 0.75,
                    boxSizing: "border-box",
                    outline: "none",
                    "&:focus": { borderColor: "#98A2B3" },
                    "&::placeholder": { color: "#98A2B3", opacity: 1 },
                } }) }));
    }
    const hasValue = (value ?? "").trim() !== "";
    const collapsed = (_jsx(Box, { onClick: () => setEditing(true), sx: { px: 1, minHeight: 24, display: "flex", alignItems: "center", cursor: "pointer" }, children: _jsx(Typography, { sx: {
                fontSize: "14px",
                color: hasValue ? "#27272a" : "#98A2B3",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "250px",
            }, children: hasValue ? value : "Note" }) }));
    // Long notes are truncated to ~250px on one line (…); full text shows on hover.
    return hasValue ? (_jsx(HoverTooltip, { title: value, arrow: true, placement: "left", children: collapsed })) : collapsed;
});
const PerformanceReportTab = ({ rows, totals, metricWord = "Sales", isOrders = false, onSaveModifiers, savingModifiers = false, onSaveDayEdit }) => {
    const columns = useMemo(() => buildColumns(metricWord), [metricWord]);
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = useState("date");
    const [sortOrder, setSortOrder] = useState("asc");
    const [search, setSearch] = useState("");
    // ── Column visibility ─────────────────────────────────────
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnVisibilityAnchorEl, setColumnVisibilityAnchorEl] = useState(null);
    // Editable Modifier / Notes / Daily-actual cells (local, keyed by row id = ISO date).
    const [modifiers, setModifiers] = useState({});
    const [notes, setNotes] = useState({});
    const [dailyActuals, setDailyActuals] = useState({});
    // Month total of the API-provided daily values (api_act_daily_sale), shown as a
    // small second line under the Daily column's Total. A manual entry replaces
    // act_daily_sale — and so moves the bold total — but never the API value, so this
    // line stays a fixed reference for what the networks actually reported.
    const apiActualDailyTotal = useMemo(() => rows.reduce((sum, r) => sum + r.apiActualDailySale, 0), [rows]);
    // Only past days (strictly before today) are editable — today + future are projected.
    const todayYmd = useMemo(() => {
        const n = new Date();
        return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
    }, []);
    const handleSaveModifiers = async () => {
        if (!onSaveModifiers)
            return;
        const payload = rows
            .filter((r) => {
            const edited = modifiers[r.id];
            return edited !== undefined && edited.trim() !== (r.modifier ?? "").trim();
        })
            .map((r) => ({ date: r.id, modifier: (modifiers[r.id] ?? "").trim() }));
        if (payload.length === 0)
            return; // nothing edited — no request
        try {
            await onSaveModifiers(payload);
            setModifiers({}); // clear overrides so the refreshed report values show
        }
        catch {
            /* error toast handled by the caller */
        }
    };
    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q)
            return rows;
        return rows.filter((r) => r.date.toLowerCase().includes(q));
    }, [rows, search]);
    const filteredCount = filteredRows.length;
    React.useEffect(() => { setPage(1); }, [filteredCount]);
    const sortedRows = useMemo(() => {
        const accessor = SORT_ACCESSORS[sortBy];
        if (!accessor)
            return filteredRows;
        return [...filteredRows].sort((a, b) => {
            const va = accessor(a);
            const vb = accessor(b);
            if (va < vb)
                return sortOrder === "asc" ? -1 : 1;
            if (va > vb)
                return sortOrder === "asc" ? 1 : -1;
            return 0;
        });
    }, [filteredRows, sortBy, sortOrder]);
    const totalPages = Math.max(1, Math.ceil(sortedRows.length / ITEMS_PER_PAGE));
    const pagedRows = useMemo(() => {
        const start = (page - 1) * ITEMS_PER_PAGE;
        return sortedRows.slice(start, start + ITEMS_PER_PAGE);
    }, [sortedRows, page]);
    const renderCell = (row, col) => {
        switch (col.id) {
            case "date":
                return _jsx(Box, { sx: { px: 1 }, children: _jsx(Typography, { sx: { fontSize: "14px !important", color: "#27272a", whiteSpace: "nowrap" }, children: row.date }) });
            case "actual_daily": {
                // Past days (before today) are editable — click to reveal the box.
                // Today + future stay projected (plain value). The ⓘ (original value
                // tooltip) shows only on days that were manually updated; a day the
                // user has just edited counts too, before the refetch lands.
                const isEditable = row.id < todayYmd;
                if (!isEditable)
                    return isOrders ? _jsx(CountText, { value: row.actualDailySales }) : _jsx(CurrencyText, { value: row.actualDailySales });
                const displayValue = dailyActuals[row.id] != null ? Number(dailyActuals[row.id]) : row.actualDailySales;
                return (_jsx(DailyActualCell, { value: displayValue, apiValue: row.apiActualDailySale, placeholder: metricWord, isOrders: isOrders, isManual: row.manualUpdated != null || dailyActuals[row.id] != null, onCommit: async (v) => {
                        const clean = v.replace(/[^0-9.-]/g, "");
                        if (clean === String(Math.round(row.actualDailySales)))
                            return; // unchanged
                        // Hold the typed value in the display until the refetch lands.
                        setDailyActuals((p) => ({ ...p, [row.id]: clean }));
                        await onSaveDayEdit?.({ date: row.id, updated_value: clean });
                        // Drop the local override so the recomputed value shows.
                        setDailyActuals((p) => { const n = { ...p }; delete n[row.id]; return n; });
                    } }));
            }
            case "actual_mtd": return isOrders ? _jsx(CountText, { value: row.actualMtdSales }) : _jsx(CurrencyText, { value: row.actualMtdSales });
            case "diff": return _jsx(Box, { sx: { px: 1 }, children: _jsx(DiffChip, { percentage: row.diff.percentage, trend: row.diff.trend }) });
            case "original_daily": return isOrders ? _jsx(CountText, { value: row.originalDailySales }) : _jsx(CurrencyText, { value: row.originalDailySales });
            case "modifier":
                return (_jsx(ModifierCell, { value: (modifiers[row.id] ?? row.modifier ?? "").replace("%", ""), onCommit: (v) => setModifiers((p) => ({ ...p, [row.id]: v.replace("%", "") })) }));
            case "original_mtd": return isOrders ? _jsx(CountText, { value: row.originalMtdSales }) : _jsx(CurrencyText, { value: row.originalMtdSales });
            case "mtd_original": return row.mtdCommissionOriginal == null ? _jsx(Dash, {}) : _jsx(CurrencyText, { value: row.mtdCommissionOriginal });
            case "mtd_actual": return row.mtdCommissionActualProjected == null ? _jsx(Dash, {}) : _jsx(CurrencyText, { value: row.mtdCommissionActualProjected });
            case "notes":
                return (_jsx(NotesCell, { value: notes[row.id] ?? row.notes, onCommit: (v) => {
                        setNotes((p) => ({ ...p, [row.id]: v })); // keep the display in sync (no refetch)
                        if ((v ?? "") !== (row.notes ?? ""))
                            onSaveDayEdit?.({ date: row.id, notes: v });
                    } }));
            default: return null;
        }
    };
    const renderSummaryRow = (_rows, col) => {
        switch (col.id) {
            case "date": return _jsx(Box, { sx: { px: 1 }, children: _jsx(Typography, { sx: { fontWeight: 700, color: "#181d27", fontSize: "14px !important" }, children: "Total" }) });
            case "actual_daily":
                // Bold = the effective total (moves with manual entries); the small
                // line under it = the API total, which manual entries never touch.
                return (_jsxs(Box, { children: [isOrders ? _jsx(CountText, { value: totals.actualDailySales, bold: true }) : _jsx(CurrencyText, { value: totals.actualDailySales, bold: true }), Math.round(Number(apiActualDailyTotal)) !== totals.actualDailySales && (_jsx(HoverTooltip, { title: "Total reported by the API \u2014 unaffected by manual updates", arrow: true, placement: "right", children: _jsx(Box, { sx: { display: "inline-block", cursor: "help" }, children: isOrders ? _jsx(SubCountText, { value: apiActualDailyTotal }) : _jsx(SubCurrencyText, { value: apiActualDailyTotal }) }) }))] }));
            case "actual_mtd": return isOrders ? _jsx(CountText, { value: totals.actualMtdSales, bold: true }) : _jsx(CurrencyText, { value: totals.actualMtdSales, bold: true });
            case "original_daily": return isOrders ? _jsx(CountText, { value: totals.originalDailySales, bold: true }) : _jsx(CurrencyText, { value: totals.originalDailySales, bold: true });
            case "original_mtd": return isOrders ? _jsx(CountText, { value: totals.originalMtdSales, bold: true }) : _jsx(CurrencyText, { value: totals.originalMtdSales, bold: true });
            case "mtd_original": return _jsx(CurrencyText, { value: totals.mtdCommissionOriginal, bold: true });
            case "mtd_actual": return _jsx(CurrencyText, { value: totals.mtdCommissionActualProjected, bold: true });
            default: return "";
        }
    };
    const showSummaryRow = pagedRows.length > 0;
    return (_jsxs(Box, { sx: { bgcolor: "#fff", border: "1px solid #EAECF0", borderRadius: "8px", overflow: "hidden" }, children: [_jsx(TableToolbar, { title: "", showSearch: true, searchValue: search, onSearchChange: setSearch, searchPlaceholder: "Search date...", showRows: true, enableColumnVisibility: true, onColumnVisibilityClick: (e) => setColumnVisibilityAnchorEl(e.currentTarget), showFullScreen: true, customActions: _jsx(Button, { variant: "contained", disableElevation: true, disabled: savingModifiers, sx: {
                        bgcolor: "#FF008E",
                        "&:hover": { bgcolor: "#e60081" },
                        "&.Mui-disabled": { bgcolor: "#F9A8D4", color: "#fff" },
                        textTransform: "none",
                        fontWeight: 600,
                        borderRadius: "8px",
                        height: "36px",
                        px: 2,
                        whiteSpace: "nowrap",
                    }, onClick: handleSaveModifiers, children: savingModifiers ? "Saving..." : "Save Modifier" }) }), _jsx(Box, { className: "table-container", sx: {
                    ...commissionTableContainerSx,
                    position: "relative",
                    borderRadius: 0,
                    borderLeft: "none",
                    borderRight: "none",
                    borderBottom: "none",
                    // Force the Notes column wide. With table-layout:auto the column's
                    // `width` is only a hint and the short collapsed text lets the browser
                    // shrink it, so pin a hard min-width on its header + body cells; the
                    // table then overflows its container and scrolls horizontally.
                    '& th[data-column-id="notes"], & td[data-column-id="notes"]': { minWidth: "250px" },
                    '& th[data-column-id="modifier"], & td[data-column-id="modifier"]': { minWidth: "130px" },
                }, children: _jsx(CommonTable, { columns: columns, rows: pagedRows, renderCell: renderCell, 
                    // Tint the "actual" days (before today — the rows showing the ⓘ
                    // tooltip) so the actual→projected boundary is visible. The
                    // `.reusable-table-cell:first-of-type` selector out-specifies the
                    // sticky Date-cell's white background, so no !important is needed.
                    rowSx: (row) => row.id < todayYmd ? {
                        "& td.reusable-table-cell": { backgroundColor: "#F2F4F7" },
                        "& td.reusable-table-cell:first-of-type": { backgroundColor: "#F2F4F7" },
                    } : undefined, sortConfig: { columnId: sortBy, direction: sortOrder }, onSortChange: (id, dir) => { setSortBy(id); setSortOrder(dir || (sortOrder === "asc" ? "desc" : "asc")); }, expandTrigger: EXPAND_TRIGGERS.NONE, enableColumnActions: false, enableColumnReorder: false, enableColumnPinning: false, enableColumnFilters: false, enableColumnVisibility: false, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchorEl: columnVisibilityAnchorEl, onColumnVisibilityClose: () => setColumnVisibilityAnchorEl(null), columnVisibilityConfig: { enable: true, columns: HIDEABLE_COLUMN_IDS, enableReorder: false }, enableSelection: false, enableSummaryRow: showSummaryRow, renderSummaryRow: showSummaryRow ? renderSummaryRow : undefined, summaryRowPosition: SUMMARY_ROW_POSITIONS.BOTTOM, type: TABLE_TYPES.GROUPED, isLoading: false, stickyHeader: true, maxHeight: "calc(100vh - 140px)", emptyMessage: "No report data available." }) }), _jsx(Box, { className: "user-list-pagination-wrapper", children: _jsx(Pagination, { currentPage: 1, totalPages: 1, onPageChange: setPage, maxVisiblePages: 5, totalEntries: sortedRows.length, itemsPerPage: ITEMS_PER_PAGE }) })] }));
};
export default PerformanceReportTab;
